import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** May be async — the dialog shows a spinner and stays open until it settles. */
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [isPending, setIsPending] = useState(false);

  const handleConfirm = async () => {
    setIsPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Left open so the caller's toast is visible against the context that
      // produced it, and the user can retry without re-navigating.
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-request would strand an in-flight mutation behind a
        // dialog that no longer exists.
        if (!isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => void handleConfirm()}
            loading={isPending}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Manages the open state and the pending row for a confirm flow.
 *
 * @example
 * const confirm = useConfirm<Employee>();
 * <Button onClick={() => confirm.ask(employee)}>Delete</Button>
 * <ConfirmDialog {...confirm.props} onConfirm={() => remove(confirm.target!.id)} … />
 */
export function useConfirm<T = void>() {
  const [target, setTarget] = useState<T | null>(null);

  return {
    target,
    ask: (value: T) => setTarget(value),
    close: () => setTarget(null),
    props: {
      open: target !== null,
      onOpenChange: (open: boolean) => {
        if (!open) setTarget(null);
      },
    },
  };
}
